import { Box, FormLabel, Grid, HStack, Text } from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React, { useCallback, useMemo } from "react";
import DevicePicker from "../../controls/common/DevicePicker";
import useDevice from "../../hooks/useDevice";
import { flatMap } from "lodash";

export interface LayoutOption {
	id: string;
	attrKey: string;
	value: string;
	title?: string;
	options: {
		value: string;
		icon: React.ElementType;
		label?: string;
	}[];
}

interface LayoutOptionsRendererProps {
	layoutsData: LayoutOption[];
	selectedValues: Record<string, string>;
	onChange: (attrKey: string, value: string, layoutId?: string) => void;
	currentLayout: string;
	columns?: number;
}

const cardSize = 70;

const LayoutOptionsRenderer: React.FC<LayoutOptionsRendererProps> = ({
	layoutsData,
	selectedValues,
	onChange,
	currentLayout,
	columns,
}) => {
	const { device, setDevice } = useDevice();

	const flatOptions = useMemo(
		() =>
			layoutsData.flatMap((ld) =>
				ld.options.map((opt) => ({
					...opt,
					attrKey: ld.attrKey,
					layoutId: ld.id,
					layoutTitle: ld.title,
				}))
			),
		[layoutsData]
	);

	const handleClick = useCallback(
		(attrKey: string, value: string, layoutId?: string) => {
			onChange(attrKey, value, layoutId);
		},
		[onChange]
	);

	const selectionsMap = useMemo(
		() => ({ ...selectedValues }),
		[selectedValues]
	);

	const multipleSelected = useMemo(() => {
		return Object.values(selectionsMap).filter((v) => !!v).length > 1;
	}, [selectionsMap]);

	return (
		<>
			<HStack align="center" justify="space-between" mb="2">
				<HStack align="center">
					<FormLabel>{__("Presets", "magazine-blocks")}</FormLabel>
					<DevicePicker device={device} setDevice={setDevice} />
				</HStack>
			</HStack>
			<Grid
				templateColumns={{
					base: "repeat(1, 1fr)",
					md: `repeat(${columns || 3}, 1fr)`,
				}}
				gap={3}
			>
				{flatOptions.map((opt, idx) => {
					const isSelected = multipleSelected
						? currentLayout === opt.layoutId &&
						  selectionsMap[opt.attrKey] === opt.value
						: selectionsMap[opt.attrKey] === opt.value;

					return (
						<Box
							bg="gray.50"
							key={opt.value}
							role="button"
							tabIndex={0}
							onClick={() =>
								handleClick(
									opt.attrKey,
									opt.value,
									opt.layoutId
								)
							}
							borderRadius="base"
							border={isSelected ? "2px solid" : "1px solid"}
							borderColor={isSelected ? "purple.500" : "gray.200"}
							height={`${cardSize}px`}
							display="flex"
							flexDirection="column"
							alignItems="center"
							justifyContent="center"
							cursor="pointer"
							p={1}
						>
							<Box as={opt.icon} width="full" height="full" />
							<Text
								fontSize="xs"
								fontWeight={"semibold"}
								mb={0}
								pt={0.5}
							>
								{__("Style", "magazine-blocks")} {idx + 1}
							</Text>
						</Box>
					);
				})}
			</Grid>
		</>
	);
};

export default LayoutOptionsRenderer;
